// src/pages/ventes/Factures.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Badge,
    Spinner, Alert, Tooltip, OverlayTrigger, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { STATUT_FACTURE, MODE_REGLEMENT } from "../utils/constantes";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   HELPERS — détermine la nature d'une facture
   ============================================================ */
const getNatureFacture = (facture) => {
    const nbProduits = parseInt(facture.nb_produits || 0, 10);
    const nbServices = parseInt(facture.nb_services || 0, 10);

    if (nbServices > 0 && nbProduits === 0) return 'service';
    if (nbProduits > 0 && nbServices === 0) return 'produit';
    if (nbProduits > 0 && nbServices > 0)   return 'mixte';
    return 'inconnu';
};

export default function Factures() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [filtreTypeContenu, setFiltreTypeContenu] = useState('tous');
    const [sortConfig, setSortConfig] = useState({ key: 'date_facture', direction: 'desc' });
    const [taxes, setTaxes] = useState({
        tva_taux: 18, css_taux: 1, tva_active: true, css_active: true
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchFactures();
        fetchStats();
        fetchTaxes();
    }, [filtreStatut, filtreTypeContenu]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchFactures = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const params = new URLSearchParams();
            if (filtreStatut !== 'tous') params.append('statut', filtreStatut);
            if (filtreTypeContenu !== 'tous') params.append('type_contenu', filtreTypeContenu);

            const url = `${API_BASE_URL}/factures.php${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setFactures(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    /* ============================================================
       HELPERS STATUT
       ============================================================ */
    const getStatutConfig = (statut) => {
        const config = STATUT_FACTURE[statut] || STATUT_FACTURE.brouillon;
        const gradientMap = {
            brouillon:        'linear-gradient(135deg, #64748b, #475569)',
            emise:            'linear-gradient(135deg, #3b82f6, #2563eb)',
            envoye:           'linear-gradient(135deg, #3b82f6, #2563eb)',
            accepte:          'linear-gradient(135deg, #10b981, #059669)',
            refuse:           'linear-gradient(135deg, #ef4444, #dc2626)',
            payee:            'linear-gradient(135deg, #10b981, #059669)',
            impayee:          'linear-gradient(135deg, #ef4444, #dc2626)',
            payee_partielle:  'linear-gradient(135deg, #f59e0b, #d97706)',
            annulee:          'linear-gradient(135deg, #dc2626, #b91c1c)'
        };
        return {
            gradient: gradientMap[statut] || gradientMap.brouillon,
            label: config.label,
            icon: config.icon || 'ri-file-line'
        };
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const getJoursRetard = (dateEcheance) => {
        const today = new Date();
        const echeance = new Date(dateEcheance);
        const diffTime = today - echeance;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getTypeContenuBadge = (facture) => {
        const nature = getNatureFacture(facture);
        const nbProduits = parseInt(facture.nb_produits || 0, 10);
        const nbServices = parseInt(facture.nb_services || 0, 10);

        if (nature === 'service') {
            return (
                <span className="badge-contenu-service" title={`${nbServices} service(s)`}>
                    <i className="ri-service-line"></i> Service
                </span>
            );
        }
        if (nature === 'produit') {
            return (
                <span className="badge-contenu-produit" title={`${nbProduits} produit(s)`}>
                    <i className="ri-box-3-line"></i> Produit
                </span>
            );
        }
        if (nature === 'mixte') {
            return (
                <span className="badge-contenu-mixte" title={`${nbProduits} produit(s) + ${nbServices} service(s)`}>
                    <i className="ri-links-line"></i> Mixte
                </span>
            );
        }
        return null;
    };

    /* ============================================================
       FILTER / SORT / PAGINATION
       ============================================================ */
    const filteredFactures = useMemo(() => {
        return factures
            .filter(f =>
                f.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_facture') {
                    return sortConfig.direction === 'asc'
                        ? new Date(a.date_facture) - new Date(b.date_facture)
                        : new Date(b.date_facture) - new Date(a.date_facture);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc'
                        ? a.net_a_payer - b.net_a_payer
                        : b.net_a_payer - a.net_a_payer;
                }
                return 0;
            });
    }, [factures, filterText, sortConfig]);

    const indexOfLastItem  = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems     = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages       = Math.ceil(filteredFactures.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ icon, onClick, tooltip, disabled = false, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                disabled={disabled}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
   

       if (loading && !factures.length) {
           
                   return (
                       <>
                           <Header />
                           <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                               <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                   <div className="text-center" style={{ maxWidth: '400px' }}>
                                       <div className="loading-ring-wrapper mb-4">
                                           <div className="loading-ring"></div>
                                           <div className="loading-ring-inner"></div>
                                           <i className="ri-loader-4-fill loading-icon"></i>
                                       </div>
                                       <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                           Chargement des données...
                                       </h5>
                                       <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                       <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                           <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                               <div
                                                   className="progress-bar progress-bar-striped progress-bar-animated"
                                                   style={{
                                                       width: '100%',
                                                       background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                       backgroundSize: '200% 100%',
                                                       animation: 'shimmer 1.5s linear infinite'
                                                   }}
                                               />
                                           </div>
                                       </div>
                                   </div>
                               </div>
                           </div>
                           <Footer />
                           <style>{`
                               .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                               .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                               .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                               .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                               @keyframes spin { to { transform: rotate(360deg); } }
                               @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                               @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                           `}</style>
                       </>
                   );
               }

    if (error && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchFactures} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-list-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Gestion des factures</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-list-line"></i>
                                        {filteredFactures.length} facture(s)
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-filter-line"></i>
                                        {filtreStatut === 'tous' ? 'Tous les statuts' : filtreStatut}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchFactures} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/ventes/factures/nouveau')}>
                                <i className="ri-add-line"></i> Nouvelle facture
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-file-list-3-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Factures (mois)</span>
                                        <h3 className="kpi-value-charte">{stats.mois?.nb_factures || 0}</h3>
                                        <span className="kpi-unit-charte">{formatCurrency(stats.mois?.total_ca || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-check-double-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Encaissé</span>
                                        <h3 className="kpi-value-charte">{formatCurrency(stats.mois?.total_regle || 0)}</h3>
                                        <span className="kpi-unit-charte">Réglé</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-warning">
                                    <div className="kpi-icon-charte"><i className="ri-time-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Impayés</span>
                                        <h3 className="kpi-value-charte">{stats.impayes?.nb || 0}</h3>
                                        <span className="kpi-unit-charte">{formatCurrency(stats.impayes?.total || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-service">
                                    <div className="kpi-icon-charte"><i className="ri-service-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Factures avec services</span>
                                        <h3 className="kpi-value-charte">
                                            {factures.filter(f => (parseInt(f.nb_services, 10) || 0) > 0).length}
                                        </h3>
                                        <span className="kpi-unit-charte">Sur {factures.length} au total</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher facture, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="brouillon">Brouillons</option>
                                        <option value="emise">Émises</option>
                                        <option value="payee_partielle">Paiements partiels</option>
                                        <option value="payee">Payées</option>
                                        <option value="impayee">Impayées</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filtreTypeContenu}
                                        onChange={(e) => setFiltreTypeContenu(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Tout contenu</option>
                                        <option value="produits">Produits uniquement</option>
                                        <option value="services">Services uniquement</option>
                                        <option value="mixte">Mixte</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <div className="text-end">
                                        <span className="count-badge-charte">
                                            <i className="ri-stack-line"></i>
                                            {filteredFactures.length}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-file-list-3-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des factures</span>
                                    <span className="badge-header-light">{filteredFactures.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">N° Facture</th>
                                            <th>Client</th>
                                            <SortableHeader field="montant" className="text-end">Montant TTC</SortableHeader>
                                            <th className="text-start">Réglé</th>
                                            <th className="text-start">Reste</th>
                                            <th>Échéance</th>
                                            <th className="text-center">Contenu</th>
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((facture) => {
                                                const nature      = getNatureFacture(facture);
                                                const isService   = nature === 'service';
                                                const reste       = facture.net_a_payer - (facture.montant_regle || 0);
                                                const joursRetard = getJoursRetard(facture.date_echeance);
                                                const estEnRetard = joursRetard > 0 && facture.statut !== 'payee' && facture.statut !== 'annulee';

                                                return (
                                                    <tr key={facture.id_facture} className={estEnRetard ? 'row-late-charte' : ''}>
                                                        <td className="ps-4">
                                                            <Link
                                                                to={`/ventes/facturesDetails/${facture.id_facture}`}
                                                                className="table-link-charte"
                                                            >
                                                                {facture.numero_facture}
                                                            </Link>
                                                        </td>
                                                        <td style={{ minWidth: '140px' }}>
                                                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                {facture.client_nom}
                                                            </div>
                                                            <small className="text-muted">{facture.code_client}</small>
                                                        </td>
                                                        <td className="text-start fw-bold" style={{ color: COLORS.primaryDark }}>
                                                            {formatCurrency(facture.net_a_payer)}
                                                        </td>

                                                        {/* Réglé et Reste : masqués pour un service pur */}
                                                        <td className="text-start" style={{ color: COLORS.success }}>
    {formatCurrency(facture.montant_regle || 0)}
</td>
<td className={`text-start fw-semibold ${reste > 0 ? 'text-danger' : 'text-success'}`}>
    {formatCurrency(reste)}
</td>

                                                        <td>
                                                            <span className={estEnRetard ? 'text-danger fw-semibold' : 'text-muted'}>
                                                                {formatDate(facture.date_echeance)}
                                                            </span>
                                                            {estEnRetard && !isService && (
                                                                <span className="badge-late-charte">
                                                                    <i className="ri-time-line"></i>J+{joursRetard}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            {getTypeContenuBadge(facture) || (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </td>

                                                        {/* Statut : remplacé par un badge neutre pour un service pur */}
                                                        <td className="text-center">
                                                            {isService ? (
                                                                <span className="status-service-charte">
                                                                    <i className="ri-service-line"></i>
                                                                    Prestation
                                                                </span>
                                                            ) : (
                                                                getStatutBadge(facture.statut)
                                                            )}
                                                        </td>

                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {!isService && facture.statut !== 'payee' && facture.statut !== 'annulee' && (
                                                                    <ActionButton
                                                                        icon="ri-bank-card-line"
                                                                        onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                        tooltip="Encaisser"
                                                                        color={COLORS.success}
                                                                    />
                                                                )}
                                                                <ActionButton
                                                                    icon="ri-file-copy-line"
                                                                    onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                    tooltip="Créer un avoir"
                                                                    color={COLORS.warning}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune facture trouvée</h6>
                                                        <p className="text-muted small mb-0">Aucun résultat pour votre recherche</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{indexOfFirstItem + 1}</strong> à <strong>{Math.min(indexOfLastItem, filteredFactures.length)}</strong> sur <strong>{filteredFactures.length}</strong> facture(s)
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-service { border-left-color: var(--secondary); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-service .kpi-icon-charte { background: ${COLORS.secondary}15; color: var(--secondary); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== BADGES DE CONTENU ===== */
                .badge-contenu-service {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, #f0fbff, #e8f7fc);
                    color: var(--secondary);
                    padding: 0.3rem 0.7rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border: 1px solid rgba(33, 172, 205, 0.3);
                }
                .badge-contenu-produit {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9;
                    color: #64748b;
                    padding: 0.3rem 0.7rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border: 1px solid #e2e8f0;
                }
                .badge-contenu-mixte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, #fef3c7, #fde68a);
                    color: #92400e;
                    padding: 0.3rem 0.7rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border: 1px solid rgba(245, 158, 11, 0.4);
                }

                /* ===== STATUT SERVICE (remplace le badge de statut) ===== */
                .status-service-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, #e0f2fe, #bae6fd);
                    color: #0369a1;
                    padding: 0.35rem 0.9rem;
                    border-radius: 50px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    border: 1px solid rgba(3, 105, 161, 0.25);
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte { background: #fef2f2; }
                .table-charte tbody tr.row-late-charte:hover { background: #fee2e2; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                }
                .badge-late-charte {
                    display: inline-flex; align-items: center; gap: 3px;
                    background: var(--danger); color: white;
                    padding: 0.15rem 0.5rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                    margin-left: 0.5rem;
                    animation: pulse-soft 2s infinite;
                }
                @keyframes pulse-soft {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte {
                    font-size: 0.8rem; color: #64748b;
                }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }

                /* ===== ALERTE ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover:not(:disabled) {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}